import type { SagaErrorEvent } from '@ipfabric/frontend-shared-assets/utils/events/SagaErrorEvent';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { ManagedDomEventListeners } from '@ipfabric/ui-components/utils/dom/ManagedDomEventListeners';
import type { ApiErrorCode } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { setMaintenanceVisibility } from 'components/GlobalState/commands/setMaintenanceVisibility';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { useEffect } from 'react';
import type { AppTranslations } from 'translations/translations';
import { workaroundExtractModelCurrentState } from 'utils/hooks/useCommandModel';
import { useNotifications } from 'utils/hooks/useNotifications';
import { formatFlash } from 'utils/request/formatFlash';
import { isErrorEvent } from 'utils/types/isErrorEvent';

const apiErrorsToSuppressNotification: ApiErrorCode[] = [
  'API_EXPIRED_ACCESS_TOKEN',
  'API_INVALID_ACCESS_TOKEN',
  'API_MAINTENANCE_IN_PROGRESS',
  'API_INVALID_REFRESH_TOKEN',
  'API_LICENSE_NOT_FOUND',
];

export const useErrorHandler = (msg: AppTranslations): void => {
  const { showNotification } = useNotifications();
  const logError = useLogError();
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
    socketState: prop('socketState'),
  });

  // function can receive all errors, so we do not know type here yet
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  const errorNotificationHandler = (error: any) => {
    switch (error?.type) {
      case 'API': {
        const suppress =
          error.code && apiErrorsToSuppressNotification.includes(error.code);
        if (!suppress) {
          const flash = formatFlash(error, msg.request);
          const message = flash.message || false;
          const title = flash.title || undefined;

          if (!title && !message) {
            return;
          }

          showNotification({ type: 'danger', message, title });
        }

        if (error.code === 'API_MAINTENANCE_IN_PROGRESS') {
          ctxProps.dispatch(setMaintenanceVisibility, { value: true });
        }
        break;
      }
      case 'STUMBLE': {
        showNotification({
          type: 'warning',
          message: (
            <>
              The app encountered internal problem, please provide tech support
              file to IP Fabric support for log inspection.
              <br />
              <span className='font-semibold'>{error.message}</span>
            </>
          ),
          // Using message since it's specificaly describing problem and therefore there is nearly same
          // probability of writing same message as in case of dedicated string id
          snoozeKey: error.message,
          title: msg.request.issueFound,
        });
        break;
      }
      case 'INSUFFICIENT_RIGHTS': {
        showNotification({
          type: 'danger',
          message: error.message,
          title: 'Insufficient Rights',
        });
        break;
      }
      default: {
        showNotification({
          type: 'danger',
          message: error?.message,
          title: 'Unexpected Error',
        });
      }
    }
  };

  // Do not show errors if there is overlay because of socket disconnection or unavailable system
  const isApiUnavailableOverlayShown = () =>
    ctxProps.socketState === 'DISCONNECTED' ||
    // Not read via selector: request() sets it right before throwing the error and this handler
    // can run before React re-renders, so the selected value would still be false
    workaroundExtractModelCurrentState(ctxProps.dispatch).systemUnavailable;

  let lastSeenError: any;
  const createErrorHandler = useStableCallback(
    <T extends ErrorEvent | PromiseRejectionEvent | SagaErrorEvent>(
      extractError: (event: T) => any,
      origin: string,
    ) =>
      (event: T) => {
        if (
          isErrorEvent(event) &&
          event.message === 'Script error.' &&
          event.error === null
        ) {
          // https://stackoverflow.com/questions/45844565/script-error-errors-in-window-onerror-in-safari-only
          // Safari provides completely useless error to JavaScript in some cases, unfortunately
          // also in case of ResizeObserver know problem covered below, so we are not able to determine
          // whether to suppress the error or not.
          // Therefore we can't show or meaningfully log those errors in Safari, just we won't prevent
          // default to allow displaying them in console.
          return;
        }
        event.preventDefault();
        if (
          isErrorEvent(event) &&
          event.message.startsWith('ResizeObserver loop')
        ) {
          // Do not show error from ResizeObserver. https://stackoverflow.com/a/50387233/4806544
          return;
        }
        const error = extractError(event);
        const errorFunction = console.error ?? console.log; // eslint-disable-line no-console
        if (error === lastSeenError) {
          // reduce noise because synchronous errors in React are thrown twice
          // https://github.com/facebook/react/issues/10474
          errorFunction('(last error re-thrown)');
          return;
        }
        lastSeenError = error;
        errorFunction(error, event);
        logError(error, origin);
        if (!isApiUnavailableOverlayShown()) {
          errorNotificationHandler(error);
        }
      },
  );

  useEffect(() => {
    const domEventListeners = new ManagedDomEventListeners(
      [
        window,
        'error',
        createErrorHandler(
          (event: ErrorEvent) => event.error,
          'UNHANDLED ERROR',
        ),
      ],
      [
        window,
        'unhandledrejection',
        createErrorHandler(
          (event: PromiseRejectionEvent) => event.reason,
          'UNHANDLED PROMISE REJECTION',
        ),
      ],
      [
        window,
        'sagaError',
        createErrorHandler(
          (event: SagaErrorEvent) => event.detail.error,
          'SAGA',
        ),
      ],
    );

    return () => domEventListeners.remove();
  }, []);
};
